import { copyText } from '../../core/copy';
import { clear, el } from '../../core/dom';
import { makeBanner, makeChip, makeFieldHead, setChip } from '../../core/shell';
import {
	DEFAULT_DETECTORS,
	detectPii,
	SAMPLE_PII,
	type DetectorOptions,
	type PiiType,
	type RedactionMode,
} from './parse';

const LABELS: Record<PiiType, string> = {
	email: 'Emails',
	phone: 'Phone patterns',
	ipv4: 'IPv4 addresses',
	url: 'URLs',
	credit_card: 'Credit-card candidates (Luhn)',
	pan_in: 'India PAN-like',
	aadhaar: 'Aadhaar-like (caution)',
	account: 'Account-like (experimental)',
};

export function mountPii(root: HTMLElement): void {
	root.classList.add('kb-tools', 'kb-tools--pii');
	root.querySelectorAll('.kb-tools__ssr-fallback').forEach((n) => n.remove());

	const opts: DetectorOptions = { ...DEFAULT_DETECTORS };
	let mode: RedactionMode = 'token';
	let enabled = new Set<string>();
	let seedEnabled = true;
	let idle = true;

	const input = el('textarea', {
		className: 'kb-tools__textarea kb-tools__textarea--tall',
		id: 'kb-pii-input',
		rows: '10',
		spellcheck: 'false',
		'aria-describedby': 'kb-pii-status kb-pii-privacy',
	}) as HTMLTextAreaElement;
	input.placeholder = 'Paste logs, tickets, or examples to redact…';

	const output = el('textarea', {
		className: 'kb-tools__textarea kb-tools__textarea--tall',
		id: 'kb-pii-output',
		rows: '10',
		readonly: 'true',
		spellcheck: 'false',
	}) as HTMLTextAreaElement;

	const chip = makeChip('kb-pii-status');
	const matchList = el('div', { className: 'kb-tools__match-list' });
	const detectorBox = el('div', { className: 'kb-tools__detectors' });

	for (const key of Object.keys(LABELS) as PiiType[]) {
		const id = `kb-pii-det-${key}`;
		const label = el('label', { className: 'kb-tools__check', for: id });
		const cb = el('input', { type: 'checkbox', id }) as HTMLInputElement;
		cb.checked = opts[key];
		cb.addEventListener('change', () => {
			opts[key] = cb.checked;
			seedEnabled = true;
			if (!idle) {
				run();
			}
		});
		label.append(cb, document.createTextNode(' ' + LABELS[key]));
		detectorBox.appendChild(label);
	}

	const modeSelect = el('select', { className: 'kb-tools__select', id: 'kb-pii-mode' }) as HTMLSelectElement;
	for (const [value, label] of [
		['token', 'Tokens ([EMAIL_1])'],
		['mask', 'Fixed masks'],
		['irreversible', 'Irreversible [REDACTED]'],
	] as const) {
		modeSelect.appendChild(el('option', { value }, [label]));
	}
	modeSelect.addEventListener('change', () => {
		mode = modeSelect.value as RedactionMode;
		if (!idle) {
			run();
		}
	});

	const analyzeBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--primary' }, [
		'Detect & redact',
	]) as HTMLButtonElement;
	const sampleBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Load sample',
	]) as HTMLButtonElement;
	const copyBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Copy redacted',
	]) as HTMLButtonElement;
	const downloadBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Download .txt',
	]) as HTMLButtonElement;
	const clearBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Clear',
	]) as HTMLButtonElement;

	const controls = el('div', { className: 'kb-tools__controls' }, [
		el('div', {}, [
			el('div', { className: 'kb-tools__label' }, ['Detector classes']),
			detectorBox,
		]),
		el('div', {}, [
			el('label', { className: 'kb-tools__label', for: 'kb-pii-mode' }, ['Redaction mode']),
			modeSelect,
		]),
	]);

	const compose = el('div', { className: 'kb-tools__compose' }, [
		makeFieldHead(el('label', { className: 'kb-tools__label', for: 'kb-pii-input' }, ['Input text'])),
		input,
		controls,
		el('div', { className: 'kb-tools__actions' }, [
			analyzeBtn,
			sampleBtn,
			copyBtn,
			downloadBtn,
			clearBtn,
		]),
	]);

	const matchPanel = el('section', { className: 'kb-tools__panel kb-tools__panel--result' }, [
		el('div', { className: 'kb-tools__panel-head' }, [
			el('h3', { className: 'kb-tools__panel-title' }, ['Matches']),
		]),
		matchList,
	]);
	const outputPanel = el('section', { className: 'kb-tools__panel kb-tools__panel--result' }, [
		el('div', { className: 'kb-tools__panel-head' }, [
			el('label', { className: 'kb-tools__panel-title', for: 'kb-pii-output' }, ['Redacted output']),
		]),
		output,
	]);

	const results = el('div', { className: 'kb-tools__results' }, [
		el('div', { className: 'kb-tools__status-row' }, [chip]),
		el('div', { className: 'kb-tools__split' }, [matchPanel, outputPanel]),
	]);

	clear(root);
	root.append(
		makeBanner(
			'Local',
			'Text stays in this browser. Nothing is uploaded, stored, or sent to AI APIs. Regex match is not identity verification.'
		),
		el('div', { className: 'kb-tools__workspace kb-tools__workspace--pii' }, [compose, results]),
		el('p', { className: 'kb-tools__privacy', id: 'kb-pii-privacy' }, [
			'Aadhaar-like and account detectors are low-confidence format matches only — never proof of authenticity.',
		])
	);

	const run = (): void => {
		const result = detectPii(input.value, opts, mode, seedEnabled ? undefined : enabled);
		if (seedEnabled) {
			enabled = new Set(result.matches.map((m) => m.id));
			seedEnabled = false;
		}
		if (idle) {
			setChip(chip, 'Waiting for text', 'idle');
		} else if (!result.ok) {
			setChip(chip, 'Cannot scan', 'error');
		} else if (result.matches.length) {
			setChip(chip, `${result.matches.length} match(es)`, 'warn');
		} else {
			setChip(chip, 'No matches in selected classes', 'ok');
		}
		output.value = idle ? '' : result.redacted;
		clear(matchList);
		if (idle || !result.matches.length) {
			matchList.appendChild(
				el('p', { className: 'kb-tools__muted' }, [
					idle ? 'Matches appear after you detect & redact.' : 'No matches for the selected detectors.',
				])
			);
			return;
		}
		for (const match of result.matches) {
			const row = el('label', { className: 'kb-tools__match' });
			const cb = el('input', { type: 'checkbox' }) as HTMLInputElement;
			cb.checked = enabled.has(match.id);
			cb.addEventListener('change', () => {
				if (cb.checked) {
					enabled.add(match.id);
				} else {
					enabled.delete(match.id);
				}
				const next = detectPii(input.value, opts, mode, enabled);
				output.value = next.redacted;
				setChip(chip, next.message, 'ok');
			});
			row.append(
				cb,
				el('span', { className: 'kb-tools__match-meta' }, [
					`${match.type} · ${match.preview} · ${match.confidence}`,
				])
			);
			matchList.appendChild(row);
		}
	};

	analyzeBtn.addEventListener('click', () => {
		idle = false;
		seedEnabled = true;
		run();
	});
	sampleBtn.addEventListener('click', () => {
		input.value = SAMPLE_PII;
		idle = false;
		seedEnabled = true;
		run();
	});
	clearBtn.addEventListener('click', () => {
		input.value = '';
		output.value = '';
		enabled = new Set();
		seedEnabled = true;
		idle = true;
		run();
		input.focus();
	});
	copyBtn.addEventListener('click', () => {
		if (output.value) {
			void copyText(output.value, copyBtn);
		}
	});
	downloadBtn.addEventListener('click', () => {
		if (!output.value) {
			return;
		}
		const blob = new Blob([output.value], { type: 'text/plain;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = el('a', { href: url, download: 'redacted.txt' }) as HTMLAnchorElement;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);
	});

	run();
}
